import React, { useContext, useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import styled from 'styled-components';
import {
  Button,
  ButtonGroup,
  TextField,
  Popover,
  Spinner,
  Sheet
} from '@superops/ui-design-system';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  B1,
  getTranslatedText,
  getHeaderValue,
  CustomModal,
  AppContext,
  preFormatForm,
  VendorConversationConstraints,
  COLOR_MAP,
  withErrorBoundary,
  toast,
  HiddenAction,
  Editor,
  TEXT_LENGTH,
  useActivityTracker,
  T2,
  Loader,
  EventEmitter,
  AIComponent,
  fetchApi,
  getAIRephraseKeys,
  COMMON_CONSTANTS,
  AccessControl,
  SearchableEmailSelect
} from '@nile/components';
import dompurify from 'dompurify';
import { SVGIcon } from '@superops/super-icons';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import {
  APPLY_AI_PROMPT,
  AccountClient,
  CREATE_VENDOR_CONVERSATION,
  GET_DEFAULT_REPLY_TEMPLATE,
  GET_USER_PROFILE,
  GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS,
  TicketClient
} from '@nile/graphql';
import {
  GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE,
  GET_ORIGINAL_MESSAGE
} from '@nile/graphql/src/Query/ticket';
import { validate } from 'validate.js';
import { cloneDeep, isEmpty, isEqual, pick } from 'lodash';
import Tippy from '@tippyjs/react';
import { Tooltip } from 'react-clone-tippy';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { plaintextValidator } from '../../Validators/fields';
import {
  EventEmitterWithIndex,
  dompurifier,
  getAIPrompts
} from '@nile/components/src/Utils/helpers';
import SideConversationTemplateButton from './SideConversationTemplateButton';

const SenderHolder = styled.div`
  display: flex;
  margin-left: auto;
  align-items: center;
  .Polaris-Button {
    padding: 11px !important;
  }
`;
const TextEditorWrapper = styled.div`
  .Superops-TextField__Input {
    border: none;
  }
  .Superops-InlineError {
    color: #bf0711;
  }
`;
const EditorWrapper = styled.div`
  #quill-rt-editor {
    height: 100%;
    .quill {
      min-height: 200px;
      height: calc(100% - 60px);
      .ql-container {
        border: none;
        min-height: inherit;
        .ql-editor {
          padding: 0px;
          min-height: inherit;
        }
        .ql-editor.ql-blank::before {
          left: 0;
        }
      }
    }
    .ql-toolbar {
      border: none;
    }
  }
`;
const EditSheet = styled.div`
  min-width: 1000px;
  max-width: 1000px;
  background: #ffffff;
  // padding: 15px 20px;
  .header {
    padding: 16px 40px;
  }
  .content {
    height: calc(100% - 71px);
  }
  &,
  .FormContainerPage {
    height: 65vh;
    border-radius: 10px;
  }
  .title {
    font-size: 20px;
    line-height: 30px;
  }
  .ql-snow .ql-editor blockquote {
    border-left: 1px solid #cccccc !important;
  }
`;

const MailboxItem = styled.div`
  padding: 8px;
  border-radius: 5px;
  cursor: pointer;

  ${(props) => !props?.isVerified && `color: #00000080;`}

  &:hover {
    background: #ececec;
  }
`;

const MailboxItemHolder = styled.div`
  padding: 5px;
`;

const EmailStatus = styled.span`
  padding: 0 8px;
  color: #ff0055;
  svg {
    margin-right: 8px;
  }
`;

const ToolTipContainer = styled.div`
  padding: 5px 10px;
`;

const SpinnerContainer = styled.div`
  .Superops-Spinner {
    display: flex;

    svg {
      width: 12px;
      height: 12px;
      fill: #000000;
    }
  }
`;

function extractSubjectAndContent(sourceHtml) {
  // Regular expression to match the content within <subject> tags
  const subjectRegex = /<subject>(.*?)<\/subject>/;

  // Extracting the content within <subject> tags
  const [, extractedSubject] = sourceHtml?.match(subjectRegex) ?? [];

  // Removing the content within <subject> tags from the source string
  const cleanedContent = dompurifier(sourceHtml.replace(subjectRegex, ''));

  return { subject: extractedSubject?.trim(), content: cleanedContent };
}

const NewSideConversation = (props) => {
  const { plain, modalOnly, modalState, zIndex = '520' } = props;
  const [newConversationOpen, setNewConversationOpen] = useState(false);

  function toggleNewConversation() {
    if (modalOnly) {
      props?.toggleState(!modalState);
    } else {
      setNewConversationOpen(!newConversationOpen);
    }
  }

  const closeNewSideConversation = () => {
    setNewConversationOpen(false);
    if (props?.toggleState) {
      props?.toggleState(false);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe(
      'closeNewSideConversation',
      closeNewSideConversation
    );
    return function cleanup() {
      EventEmitter.unsubscribe('closeNewSideConversation');
    };
  }, []);

  return (
    <>
      <CustomModal
        zIndex={zIndex}
        open={modalOnly ? modalState : newConversationOpen}
        onClose={() => {}}
      >
        <NewSideConversationForm
          {...props}
          toggleNewConversation={toggleNewConversation}
        />
      </CustomModal>
      {plain ? (
        <Button
          plain
          onClick={toggleNewConversation}
          icon={<SVGIcon color={COLOR_MAP.blue} name="Helpcenter" />}
        >
          <FormattedMessage
            id={props.btnText || 'app.sideConversation.create.new'}
          />
        </Button>
      ) : modalOnly ? (
        <></>
      ) : (
        <Button
          primary
          onClick={toggleNewConversation}
          icon={<SVGIcon color={COLOR_MAP.white} name="Helpcenter" />}
        >
          <FormattedMessage
            id={props.btnText || 'app.sideConversation.create.new'}
          />
        </Button>
      )}
    </>
  );
};

const NewSideConversationForm = (props) => {
  const {
    intl,
    title,
    onSubmit,
    module,
    workItemId,
    updateCache,
    toggleNewConversation,
    AIPrompts,
    backgroundThreadConversation
  } = props;
  const flags = useFlags();
  const appContext = useContext(AppContext);
  const runbooksRevampFlag = flags?.unifiedRunbooks;
  const { user } = appContext;
  const currentUser = user || {};
  const [subject, setSubject] = useState('');
  const [errors, setErrors] = useState({});
  const templateRef = React.useRef(null);
  const editorRef = React.useRef(null);
  const [template, setTemplate] = useState({
    name: '',
    content: '',
    placeholdersUsed: '',
    mentions: [],
    attachments: [],
    inlineImages: []
  });
  templateRef.current = template;

  // const [showOriginalMessage, setShowOriginalMessage] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [showHiddenField, setShowHiddenField] = useState({
    cc: false,
    bcc: false
  });
  const [mailList, setMailList] = useState({
    to: [],
    cc: [],
    bcc: []
  });
  const [selectedMailBox, setSelectedMailBox] = useState(null);
  const [mailBoxes, setMailBoxes] = useState([]);

  const { data: originalMessage } = useQuery(GET_ORIGINAL_MESSAGE, {
    fetchPolicy: 'network-only',
    skip: !props.showOriginalMsg || module !== 'TICKET',
    variables: {
      input: {
        entityId: workItemId
      }
    }
  });

  const [
    defaultSideConversationTemplate,
    setDefaultSideConversationTemplate
  ] = useState(null);

  const formatEmailList = (list) => {
    const mailList = [];
    list?.forEach((item) => {
      let userData = {};
      if (item?.user?.email) {
        userData = { ...userData, email: item?.user?.email };
      }

      if (!isEmpty(userData)) {
        mailList.push(userData);
      }
    });
    return mailList;
  };

  const getDefaultSideConversationTemplate = async ({ variables }) => {
    if (!isEmpty(variables)) {
      const response = await AccountClient.query({
        query: GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE,
        variables: variables,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
      });

      const getVendorConversationContentFromTemplate =
        response?.data?.getVendorConversationContentFromTemplate;

      if (
        getVendorConversationContentFromTemplate?.subject ||
        getVendorConversationContentFromTemplate?.name
      ) {
        setSubject(
          getVendorConversationContentFromTemplate?.subject ||
            getVendorConversationContentFromTemplate?.name
        );
      }

      const toUsers = getVendorConversationContentFromTemplate?.toUsers;
      const ccUsers = getVendorConversationContentFromTemplate?.ccUsers;
      const bccUsers = getVendorConversationContentFromTemplate?.bccUsers;

      let mailUsersList = cloneDeep(mailList); // Append MailUsers here and update mailUsers state
      let hiddenFieldsUpdate = {}; // If CC and Bcc is present update them and setShowHiddenField to show them

      if (!isEmpty(toUsers)) {
        const formattedToUsersList = formatEmailList(toUsers);
        mailUsersList = { ...mailUsersList, to: formattedToUsersList };
      }

      if (!isEmpty(ccUsers)) {
        const formattedCCUsersList = formatEmailList(ccUsers);
        mailUsersList = { ...mailUsersList, cc: formattedCCUsersList };
        hiddenFieldsUpdate = { ...hiddenFieldsUpdate, cc: true };
      }

      if (!isEmpty(bccUsers)) {
        const formattedBccUsersList = formatEmailList(bccUsers);
        mailUsersList = { ...mailUsersList, bcc: formattedBccUsersList };
        hiddenFieldsUpdate = { ...hiddenFieldsUpdate, bcc: true };
      }

      // Format to, cc bcc users ad set them to mailList
      setMailList(mailUsersList);
      // If CC and BCC are present open CC and BCC
      setShowHiddenField((prevState) => ({
        ...prevState,
        ...hiddenFieldsUpdate
      }));
      setDefaultSideConversationTemplate(
        getVendorConversationContentFromTemplate
      );
    }
  };

  const [createNewVendorConversation] = useMutation(
    CREATE_VENDOR_CONVERSATION,
    {
      client: AccountClient,
      fetchPolicy: 'no-cache',
      onCompleted(res) {
        if (res.createVendorConversation) {
          toggleNewConversation();
          if (!backgroundThreadConversation) {
            toast('success', 'app.sideConversation.create.success');
          }
          useActivityTracker({
            event: `new_side_conversation_${module}`
          });
          if (onSubmit) onSubmit(res.createVendorConversation);
          if (updateCache) updateCache(res.createVendorConversation);
        }
        setSubmitLoading(false);
      },
      // update(cache, { data }) {
      //   updateCache(cache, { data });
      // },
      onError: (err) => {
        toast('error', err.message);
        setSubmitLoading(false);
      }
    }
  );

  const { loading: vendorConversationReplyRecipientsLoading } = useQuery(
    GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS,
    {
      client: AccountClient,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          workItem: {
            workId: workItemId,
            module: module
          }
        }
      },
      onCompleted({ getVendorConversationReplyRecipients }) {
        if (getVendorConversationReplyRecipients) {
          const {
            mailboxes,
            selectedMailbox
          } = getVendorConversationReplyRecipients;
          setSelectedMailBox(selectedMailbox);
          setMailBoxes(mailboxes);
        }
      }
    }
  );

  function createNewConversation(formData) {
    const { to: toUsers, subject, content, cc, bcc, attachments } = formData;
    const input = {
      subject: subject,
      content: content,
      user: {
        ...pick(currentUser, ['userId', 'firstName', 'lastName', 'email'])
      },
      toUsers: toUsers?.map((usr) => pick(usr, ['email'])),
      ccUsers: cc ? cc.map((usr) => pick(usr, ['email'])) : null,
      bccUsers: bcc ? bcc.map((usr) => pick(usr, ['email'])) : null,
      inlineImages: [],
      attachments: attachments,
      sendMail: true,
      workItem: {
        workId: workItemId,
        module: module
      }
    };
    input.fromMailbox = { boxId: formData?.fromMailboxId };
    createNewVendorConversation({
      variables: {
        input: input
      }
    });
  }

  function handleSubmit() {
    setSubmitLoading(true);
    const attachments = [...template.attachments].map((att) => {
      return { ...pick(att, ['fileName', 'fileSize', 'originalFileName']) };
    });
    const formData = {
      subject: subject,
      content: template.content,
      to: mailList.to || [],
      cc: mailList.cc,
      bcc: mailList.bcc,
      attachments: attachments || []
    };
    const validationContraints = { ...VendorConversationConstraints };

    formData.fromMailboxId = selectedMailBox?.boxId;
    validationContraints.fromMailboxId = plaintextValidator({
      mandatory: true
    });
    const validationResult = validate(
      preFormatForm(formData),
      validationContraints
    );
    if (!isEmpty(validationResult)) {
      setSubmitLoading(false);
      setErrors(validationResult);
      return;
    }
    createNewConversation(formData);
  }

  function updateSubject(val) {
    setSubject(val);
  }

  function updateMailFor(type) {
    return (item) => {
      setMailList({
        ...mailList,
        [type]: item
      });
    };
  }

  function updateTemplate(obj) {
    const clonedTemplate = cloneDeep(template);
    setTemplate({ ...clonedTemplate, ...obj });
  }

  function showHiddenFieldHandler(type) {
    setShowHiddenField({ ...showHiddenField, [type]: true });
  }

  function handleFileAttachments(files, callback) {
    setIsUploading(true);
    const formData = new FormData();
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    formData.append('module', 'VENDOR_CONVERSATION_ATTACHMENT');
    if (workItemId && module)
      formData.append(
        'details',
        JSON.stringify({
          workItem: { workId: workItemId, module: module }
        })
      );
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };

    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => response.data)
      .then((list) => {
        const updatedList = [
          ...cloneDeep(templateRef.current?.attachments),
          ...list
        ];
        setTemplate({ ...templateRef.current, attachments: updatedList });
        if (callback) {
          callback(updatedList);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => setIsUploading(false));
  }

  useEffect(() => {
    // Whenever there is changes in mailboxId and workItemId we'll call this useEffect
    if (!isEmpty(workItemId) && runbooksRevampFlag) {
      const variables = {
        input: {
          template: props?.templateId
            ? {
                templateId: props?.templateId
              }
            : null,
          workItem: {
            workId: workItemId,
            module: module
          },
          mailbox: selectedMailBox?.boxId
            ? {
                boxId: selectedMailBox?.boxId
              }
            : null
        }
      };
      getDefaultSideConversationTemplate({
        variables: variables
      });
    }
  }, [selectedMailBox?.boxId, workItemId]);

  useEffect(() => {
    const emailSignature = selectedMailBox?.emailSignature;
    const emailSignatureHtml = emailSignature
      ? `<p><br></p><p><br></p>${selectedMailBox.emailSignature}`
      : '';
    const originalMessageContent =
      originalMessage?.getTicketConversationThreadedContent;
    const originalMessageHtml = originalMessageContent
      ? `${`<p><br></p><p><br></p>${dompurifier(originalMessageContent)}`}`
      : '';

    const defaultSideConversationContent =
      defaultSideConversationTemplate?.content;
    const defaultSideConversationHtml = defaultSideConversationContent
      ? `${dompurifier(defaultSideConversationContent)}`
      : '';

    // Format template content based on originalMessageContent and defaultSideConversationContent
    const templateContent = runbooksRevampFlag
      ? `${defaultSideConversationHtml} ${originalMessageHtml}`
      : `${emailSignatureHtml} ${originalMessageHtml}`;

    setTemplate({
      ...template,
      content: templateContent,
      attachments: defaultSideConversationTemplate?.attachments || []
    });

    if (editorRef.current?.updateEditorContent) {
      editorRef.current.updateEditorContent(templateContent);
    }
  }, [
    originalMessage,
    defaultSideConversationTemplate,
    selectedMailBox?.emailSignature
  ]);

  useEffect(() => {
    EventEmitter.subscribe('imageUploading', setIsImageUploading);
    return () => {
      EventEmitter.unsubscribe('imageUploading');
    };
  }, []);

  function handleAttachmentsDeletion(fileName) {
    const updatedList = cloneDeep(template?.attachments).filter(
      (item) => item.fileName !== fileName
    );
    setTemplate({ ...template, attachments: updatedList });
  }

  function mailBoxChangeHandler(mailbox) {
    if (!isEqual(mailbox, selectedMailBox)) {
      setSelectedMailBox(mailbox);
    }
  }

  const isSendDisabled = !selectedMailBox?.outgoingDomainVerified;

  const onSummaryTypeChange = (type) => {
    return {
      variables: {
        input: {
          itemId: workItemId,
          itemType: 'TICKET',
          summaryCase: 'SIDE_CONVERSATION',
          summaryType: type
        }
      },
      summaryType: type
    };
  };

  const aiRephraseKeys = getAIRephraseKeys(intl);

  const onCopy = (html) => {
    const result = extractSubjectAndContent(html);
    setSubject((s) => result.subject ?? s);
    EventEmitterWithIndex.dispatch('insertEditorContent', result.content);
  };

  const onRephraseCopy = (content, params) => {
    if (editorRef.current?.FroalaEditorRef) {
      EventEmitter.dispatch('insertEditorContent', dompurifier(content));
    } else {
      EventEmitter.dispatch('replaceEditorContent', dompurifier(content));
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('global.ai.sideconversation.onCopy', onCopy);
    return function cleanup() {
      EventEmitter.unsubscribe('global.ai.sideconversation.onCopy');
    };
  }, []);

  const populateTemplate = (selectedTemplate) => {
    if (selectedTemplate?.subject || selectedTemplate?.name) {
      setSubject(selectedTemplate?.subject || selectedTemplate?.name);
    }

    const templateContent = selectedTemplate?.content
      ? dompurifier(selectedTemplate.content)
      : '';

    if (templateContent) {
      EventEmitter.dispatch('insertEditorContent', templateContent);
    }

    if (selectedTemplate?.attachmentList || selectedTemplate?.attachments) {
      const updatedTemplate = {
        ...template,
        attachments: [
          ...(template?.attachments || []),
          ...(selectedTemplate.attachmentList ||
            selectedTemplate.attachments ||
            [])
        ]
      };
      setTemplate(updatedTemplate);
    }
  };

  return (
    <EditSheet className="br10 fx col">
      <div className="header fx actr fx-sb divider">
        <div className="flex g10 actr">
          <div>
            <B1 className="title">
              {title ||
                getTranslatedText(intl, 'app.sideConversation.new.title')}
            </B1>
          </div>
        </div>

        <ButtonGroup>
          <Button onClick={toggleNewConversation} plainSecondary>
            <FormattedMessage id="common.cancel" />
          </Button>

          <Tooltip
            distance={15}
            title={getTranslatedText(
              intl,
              'ticket.details.mailbox.email.unverified'
            )}
            position="top-start"
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
            disabled={!isSendDisabled}
          >
            <Button
              loading={submitLoading}
              disabled={isSendDisabled || isImageUploading}
              onClick={handleSubmit}
              icon={<SVGIcon name="SendWhite" color={COLOR_MAP.white} />}
              primary
            >
              <FormattedMessage id="app.create.email.send" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </div>
      <div>
        <div>
          <div className="flex pt10 pl40 pr40 center mb10 justify-between">
            <div className="flex center">
              <div>
                <FormattedMessage id="ticket.details.chat.from" />
              </div>
              {vendorConversationReplyRecipientsLoading ? (
                <SpinnerContainer className="ml-10">
                  <Spinner size="small" />
                </SpinnerContainer>
              ) : (
                <>
                  <FromMailBoxSelector
                    allowEdit
                    mailBoxes={mailBoxes}
                    selectedMailBox={selectedMailBox}
                    mailBoxChangeHandler={mailBoxChangeHandler}
                  />
                  {!selectedMailBox?.outgoingDomainVerified && (
                    <Tippy
                      content={
                        <ToolTipContainer>
                          <FormattedMessage id="ticket.details.mailbox.email.unverified" />
                        </ToolTipContainer>
                      }
                      duration={250}
                      interactive
                      placement="right"
                      arrow={false}
                      allowHTML
                      appendTo={document.body}
                    >
                      <EmailStatus className="fx actr">
                        <SVGIcon name="InlineError" color="#ff0055" size="sm" />
                      </EmailStatus>
                    </Tippy>
                  )}
                </>
              )}
            </div>
            <div className="flex center ">
              <div className="flex actr gap-15">
                {module === 'TICKET' && (
                  <AIComponent
                    editorRef={editorRef}
                    onCopy={onRephraseCopy}
                    type="REPHRASE"
                    prompts={AIPrompts || []}
                    data={{
                      Api: APPLY_AI_PROMPT,
                      accessorKey: 'applyAIPrompt'
                    }}
                    featureName="PROMPT_BUILDER"
                    headingText={getTranslatedText(
                      intl,
                      'common.rephrased.text'
                    )}
                    headingIcon={'AIWand'}
                    loadingText={aiRephraseKeys?.loadingText}
                    successText={aiRephraseKeys?.successText}
                    copyButtonText={getTranslatedText(
                      intl,
                      'common.insert.text'
                    )}
                    copySuccessText={getTranslatedText(intl, 'common.inserted')}
                    mixpanelEventOnClick={{
                      event:
                        'Rephrased with prompt - Ticket side conversation reply',
                      data: {
                        ticketId: workItemId
                      }
                    }}
                    mixpanelEventOnCopy={{
                      event:
                        'Copy and pasted rephrased content - Ticket side conversation',
                      data: {
                        ticketId: workItemId
                      }
                    }}
                    buttonVariant="plain"
                    buttonSize="slim"
                    iconOnly
                  />
                )}
                <AccessControl
                  roleToCheck="sideConversationTemplate.view"
                  render={
                    <SideConversationTemplateButton
                      populateTemplate={populateTemplate}
                      workItemId={workItemId}
                      module={module}
                      selectedMailBox={selectedMailBox}
                    />
                  }
                />
              </div>
            </div>
          </div>
          {errors?.fromMailboxId?.[0] && (
            <div className="fx pl40 error">
              <span className="fx actr mr8">
                <SVGIcon name="warning-icon" color="#cb3b3b" />
              </span>
              {errors?.fromMailboxId?.[0]}
            </div>
          )}
        </div>

        <div className="fx p5 pl40 pr40 divider">
          <SearchableEmailSelect
            mailList={mailList.to}
            label="To:"
            placeholder={getTranslatedText(
              intl,
              'app.sideConversation.placeHolder.mail.recipients'
            )}
            updateChange={updateMailFor('to')}
            error={errors?.to?.[0]}
          />
          <SenderHolder>
            <HiddenAction
              updateState={showHiddenFieldHandler}
              showField={showHiddenField.cc}
              id="cc"
            />
            <HiddenAction
              updateState={showHiddenFieldHandler}
              showField={showHiddenField.bcc}
              id="bcc"
            />
          </SenderHolder>
        </div>
        {showHiddenField.cc && (
          <div className="fx p5 pl40 pr40 divider">
            <SearchableEmailSelect
              mailList={mailList.cc}
              label="Cc:"
              placeholder={getTranslatedText(
                intl,
                'app.sideConversation.placeHolder.mail.recipients'
              )}
              updateChange={updateMailFor('cc')}
            />
          </div>
        )}
        {showHiddenField.bcc && (
          <div className="fx p5 pl40 pr40 divider">
            <SearchableEmailSelect
              mailList={mailList.bcc}
              label="Bcc:"
              placeholder={getTranslatedText(
                intl,
                'app.sideConversation.placeHolder.mail.recipients'
              )}
              updateChange={updateMailFor('bcc')}
            />
          </div>
        )}
      </div>
      <TextEditorWrapper className="pl40 pr40 mt-10">
        <TextField
          key="Subject"
          value={subject}
          fontSize="fontMedium"
          placeholder={getTranslatedText(intl, 'app.create.email.subject')}
          type="text"
          onChange={updateSubject}
          error={errors?.subject?.[0]}
          maxLength={TEXT_LENGTH.size_5}
        />
      </TextEditorWrapper>
      <EditorWrapper
        attachments={template?.attachments.length}
        className="scroll pl40 pr40 mt-20 mb-10 fg1"
      >
        <Editor
          editorRef={editorRef}
          template={template}
          updateTemplate={updateTemplate}
          module="VENDOR_CONVERSATION"
          details={{
            workItem: { workId: workItemId, module: module }
          }}
          handleFileAttachments={handleFileAttachments}
          handleAttachmentsDeletion={handleAttachmentsDeletion}
          title="Add Attachments"
          description="Upload/drop files here"
          allowMultiple
          shouldInitialFocus
        />
      </EditorWrapper>
    </EditSheet>
  );
};

export default withErrorBoundary(injectIntl(NewSideConversation));

export function FromMailBoxSelector(props) {
  const { mailBoxes, selectedMailBox, mailBoxChangeHandler } = props;

  const [popoverActive, setPopoverActive] = useState(false);

  function togglePopoverActive() {
    setPopoverActive(!popoverActive);
  }

  if (!props.allowEdit) {
    return <T2 className="ml8">{`${selectedMailBox?.outgoingAddress}`}</T2>;
  }

  return mailBoxes.length > 1 ? (
    <Popover
      active={popoverActive}
      activator={
        <div className="pointer fx actr" onClick={togglePopoverActive}>
          <div className="flex center">
            <T2 className="ml8">{`${selectedMailBox?.outgoingAddress}`}</T2>
            <div className="ml8 flex center">
              <SVGIcon name="ArrowDown" />
            </div>
          </div>
        </div>
      }
      zIndexOverride={550}
      preferredPosition="below"
      preferredAlignment="left"
      onClose={() => setPopoverActive(false)}
    >
      <MailboxItemHolder>
        {mailBoxes.map((el) => {
          return (
            <div className="fx actr">
              <MailboxItem
                onClick={() => {
                  mailBoxChangeHandler(el);
                  togglePopoverActive();
                }}
                isVerified={el?.outgoingDomainVerified}
              >
                {el?.outgoingAddress}
              </MailboxItem>
              {!el?.outgoingDomainVerified && (
                <Tippy
                  content={
                    <ToolTipContainer className="fx">
                      <FormattedMessage id="ticket.details.mailbox.email.unverified" />
                    </ToolTipContainer>
                  }
                  duration={250}
                  interactive
                  placement="right"
                  arrow={false}
                  allowHTML
                  appendTo={document.body}
                >
                  <EmailStatus className="fx actr">
                    <SVGIcon name="InlineError" color="#ff0055" size="sm" />
                  </EmailStatus>
                </Tippy>
              )}
            </div>
          );
        })}
      </MailboxItemHolder>
    </Popover>
  ) : (
    <T2 className="ml8">{`${selectedMailBox?.outgoingAddress}`}</T2>
  );
}
